import { useParams, useNavigate, generatePath } from 'react-router-dom';
import { Box } from '@mui/material';
import { AppLayout } from '@base/ui/layouts/app-layout';
import { AppHeader } from '@base/ui/components/app-header';
import { BottomBar } from '@base/ui/components/bottom-bar';
import { ErrorBanner } from '@base/ui/components/error-banner';
import { ProductInfoCard } from '../ticket-list/product-info-card';
import { TicketInfoCard } from './ticket-info-card';
import { DefectLineDisplay } from './defect-line-display';
import { NotesDisplay } from './notes-display';
import { DetailSkeleton } from './detail-skeleton';
import { useTicketDetail } from '@base/state';
import { useBackButton } from '../router/use-back-button';
import { ROUTES } from '../router/route-constants';

export function DetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { goBack } = useBackButton();

  const { data: ticket, isLoading, isError, error, refetch } = useTicketDetail(ticketId || '');

  const handleEdit = () => {
    if (ticketId) {
      navigate(generatePath(ROUTES.EDIT, { ticketId }));
    }
  };

  const errorMessage = (() => {
    if (error instanceof Error) return error.message;
    return 'Không thể tải chi tiết báo cáo';
  })();

  return (
    <AppLayout
      header={
        <AppHeader
          title="Chi tiết báo cáo lỗi"
          onBack={goBack}
        />
      }
      bottomBar={
        <BottomBar
          leftButton={{
            label: '✏️ Sửa',
            onClick: handleEdit,
          }}
          rightButton={{
            label: 'Đóng',
            onClick: goBack,
          }}
        />
      }
    >
      <Box sx={{ p: 2 }} id="detail-page-content">
        {/* Loading State */}
        {isLoading && <DetailSkeleton />}

        {/* Error State */}
        {isError && (
          <ErrorBanner
            message={errorMessage}
            onRetry={() => refetch()}
          />
        )}

        {/* Success State */}
        {!isLoading && !isError && ticket && (
          <>
            <TicketInfoCard ticket={ticket} />
            <ProductInfoCard />

            {ticket.defect_lines && ticket.defect_lines.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {ticket.defect_lines.map((line, index) => (
                  <DefectLineDisplay
                    key={line.name || index}
                    line={line}
                    lineNumber={index + 1}
                  />
                ))}
              </Box>
            )}

            <NotesDisplay notes={ticket.notes ?? undefined} />
          </>
        )}
      </Box>
    </AppLayout>
  );
}
